import React, { useMemo, useState, useRef, useEffect } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { Environment, OrbitControls, Bounds, Center } from "@react-three/drei";
import { GLTFExporter, STLExporter } from "three-stdlib";
import { generateTube } from "@/utils/generateTube";
import { SHAPE_PRESETS } from "@/utils/shapePresets";
import { useNotifications } from "@/contexts/NotificationsContext";
import { useVault } from "@/contexts/VaultContext";
import { useFeedback } from "@/contexts/FeedbackContext";

function MeshView({ params }) {
  const data = useMemo(() => generateTube(params), [params]);
  const { positions, indices } = data;

  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3)
    );
    g.setIndex(new THREE.BufferAttribute(indices, 1));
    g.computeVertexNormals();
    return g;
  }, [positions, indices]);

  return (
    <mesh geometry={geom} scale={[0.001, 0.001, 0.001]}>
      <meshPhysicalMaterial 
        color="#f5f5f5" 
        roughness={0.35} 
        clearcoat={0.9} 
        side={THREE.FrontSide}
      />
    </mesh>
  );
}

function exportGLB(object3D, name = "mudo-vessel.glb") {
  const exporter = new GLTFExporter();
  exporter.parse(
    object3D,
    (result) => {
      const blob = new Blob([result], { type: "model/gltf-binary" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    },
    { binary: true }
  );
}

function exportSTL(object3D, name = "mudo-vessel.stl") {
  const exporter = new STLExporter();
  const stl = exporter.parse(object3D);
  const blob = new Blob([stl], { type: "model/stl" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ParametricBuilder({ onMetricsChange }) {
  const { notifyAndInbox } = useNotifications() || {};
  const { saveFile } = useVault?.() || { saveFile: async () => {} };
  const { play } = useFeedback?.() || { play: () => {} };
  const controlsRef = useRef();

  const defaultPresetKey = "mug";
  const defaultParams = SHAPE_PRESETS[defaultPresetKey].params;

  const [shapeKey, setShapeKey] = useState(defaultPresetKey);
  const [params, setParams] = useState(defaultParams);

  const metrics = useMemo(() => {
    const data = generateTube(params);
    return data.metrics;
  }, [params]);

  // Feed metrics to parent whenever they change
  useEffect(() => {
    if (onMetricsChange && metrics) {
      onMetricsChange(metrics);
    }
  }, [metrics, onMetricsChange]);

  const update = (k, v) => setParams((p) => ({ ...p, [k]: v }));
  const updateSection = (i, v) =>
    setParams((p) => {
      const sections = [...p.sections];
      sections[i] = v;
      return { ...p, sections };
    });

  const applyPreset = (key) => {
    const preset = SHAPE_PRESETS[key];
    if (!preset) return;
    setShapeKey(key);
    setParams(preset.params);
    play?.("tap");
  };

  const onSave = async () => {
    const record = {
      kind: "parametricShape",
      shapeKey,
      createdAt: new Date().toISOString(),
      params,
      metrics,
    };
    try {
      await saveFile?.(`designs/vessels/${Date.now()}.json`, record);
      notifyAndInbox?.({
        notification: {
          id: `design-vault-saved-${Date.now()}`,
          title: "Designs",
          message: "Saved to Vault",
          level: "success",
          source: "system",
          world: "studio",
          action: { kind: "NAVIGATE", to: "/dashboard/templates" },
        },
      });
    } catch (e) {
      notifyAndInbox?.({
        notification: {
          id: `design-vault-save-failed-${Date.now()}`,
          title: "Designs",
          message: "Save failed",
          level: "error",
          source: "system",
          world: "studio",
          action: { kind: "NAVIGATE", to: "/dashboard/templates" },
        },
      });
    }
  };

  const onExport = (kind) => {
    const root = new THREE.Group();
    const { positions, indices } = generateTube(params);
    const g = new THREE.BufferGeometry();
    g.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(positions, 3)
    );
    g.setIndex(new THREE.BufferAttribute(indices, 1));
    g.computeVertexNormals();
    const mesh = new THREE.Mesh(g, new THREE.MeshNormalMaterial());
    root.add(mesh);

    if (kind === "glb") exportGLB(root);
    else exportSTL(root);
  };

  // Calculate total height for grid positioning
  const totalHeight = useMemo(() => {
    return params.height || 100;
  }, [params.height]);

  // Grid should be positioned at the bottom of the object (negative half-height)
  const gridYPosition = useMemo(() => {
    return -totalHeight * 0.0005; // Scale factor matches the mesh scale of 0.001
  }, [totalHeight]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
      {/* Viewer - 2 columns */}
      <div
  className="col-span-2 rounded-lg overflow-hidden h-full min-h-[384px] relative"
  style={{ background: "var(--color-surface)" }}
>

        <button
          onClick={() => {
            play?.("tap");
            controlsRef.current?.reset();
          }}
          className="absolute top-2 right-2 z-10 px-3 py-1 rounded text-sm text-white transition-all hover:brightness-110"
          style={{ background: 'var(--color-primary)' }}
        >
          Reset View
        </button>

        <Canvas camera={{ position: [0.4, 0.4, 0.8], fov: 45, near: 0.01, far: 10 }}>
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} intensity={1} />
          <directionalLight position={[-5, -5, -5]} intensity={0.3} />
          <Environment preset="studio" />
          <OrbitControls ref={controlsRef} makeDefault enablePan={true} />
          <gridHelper args={[2, 20]} position={[0, gridYPosition, 0]} />
          <Bounds fit clip observe margin={1.2}>
            <Center>
              <MeshView params={params} />
            </Center>
          </Bounds>
        </Canvas>
      </div>

      {/* Controls - 1 column */}
      <div className="space-y-4">
        {/* Shape presets */}
        <div>
          <label className="block text-sm text-white mb-2">Shape Template</label>
          <div className="grid grid-cols-2 gap-1">
            {["mug", "plate", "vase", "bowl", "planter"].map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => applyPreset(key)}
                className={`px-2 py-1 rounded text-xs text-white transition-all ${
                  shapeKey === key
                    ? ""
                    : "bg-gray-700 hover:bg-gray-600"
                }`}
                style={shapeKey === key ? { background: 'var(--color-primary)' } : {}}
              >
                {SHAPE_PRESETS[key].label}
              </button>
            ))}
          </div>
        </div>

        {/* Base polygon */}
        <div>
          <label className="block text-sm text-white">Base Polygon</label>
          <select
            value={params.sides}
            onChange={(e) => {
              const nextSides = parseInt(e.target.value, 10);
              // Keep sampling resolution aligned to side count to avoid seams/self-intersections
              update("sides", nextSides);
              update("radialSeg", nextSides);
              play?.("tap");
            }}
            className="w-full p-2 bg-gray-800 rounded text-white"
          >
            <option value={3}>Triangle</option>
            <option value={4}>Square</option>
            <option value={5}>Pentagon</option>
            <option value={6}>Hexagon</option>
            <option value={8}>Octagon</option>
            <option value={10}>Decagon</option>
            <option value={12}>Dodecagon</option>
            <option value={68}>Cylinder (68 facets)</option>
          </select>
          <style>{`
            select option {
              color: #111 !important;
              background: #fff !important;
            }
          `}</style>
        </div>

        {/* Morph to circle */}
        <Slider
          label="Morph to Circle"
          min={0}
          max={1}
          step={0.01}
          value={params.morph}
          onChange={(v) => update("morph", v)}
          onBlur={() => play?.("tap")}
        />

        {/* 5 sections */}
        <div className="grid grid-cols-2 gap-2">
          <Slider
            label="Bottom (mm)"
            min={20}
            max={180}
            step={1}
            value={params.sections[0]}
            onChange={(v) => updateSection(0, v)}
            onBlur={() => play?.("tap")}
          />
          <Slider
            label="Mid-Down (mm)"
            min={20}
            max={180}
            step={1}
            value={params.sections[1]}
            onChange={(v) => updateSection(1, v)}
            onBlur={() => play?.("tap")}
          />
          <Slider
            label="Middle (mm)"
            min={20}
            max={180}
            step={1}
            value={params.sections[2]}
            onChange={(v) => updateSection(2, v)}
            onBlur={() => play?.("tap")}
          />
          <Slider
            label="Mid-Up (mm)"
            min={20}
            max={180}
            step={1}
            value={params.sections[3]}
            onChange={(v) => updateSection(3, v)}
            onBlur={() => play?.("tap")}
          />
          <Slider
            label="Top (mm)"
            min={20}
            max={180}
            step={1}
            value={params.sections[4]}
            onChange={(v) => updateSection(4, v)}
            onBlur={() => play?.("tap")}
          />
        </div>

        <Slider
          label="Height (mm)"
          min={20}
          max={320}
          step={1}
          value={params.height}
          onChange={(v) => update("height", v)}
          onBlur={() => play?.("tap")}
        />

        <Slider
          label="Twist (°)"
          min={-180}
          max={180}
          step={1}
          value={params.twistDeg}
          onChange={(v) => update("twistDeg", v)}
          onBlur={() => play?.("tap")}
        />

        <Slider
          label="Wall (mm)"
          min={2}
          max={10}
          step={0.1}
          value={params.wall}
          onChange={(v) => update("wall", v)}
          onBlur={() => play?.("tap")}
        />

        <Slider
          label="Base (mm)"
          min={3}
          max={20}
          step={0.5}
          value={params.base}
          onChange={(v) => update("base", v)}
          onBlur={() => play?.("tap")}
        />

        {/* Metrics display */}
        <div className="bg-gray-800 p-3 rounded space-y-1 text-sm text-white">
          <div className="font-semibold text-white mb-1">Metrics</div>
          <div>Volume: {metrics.volume_cm3?.toFixed(2) || 0} cm³</div>
          <div>Surface: {metrics.surfaceArea_cm2?.toFixed(2) || 0} cm²</div>
        </div>

        {/* Export buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded text-white text-xs flex-1 transition-all hover:brightness-110"
            style={{ background: 'var(--color-primary)' }}
            onClick={onSave}
          >
            Save
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded bg-green-600 hover:bg-green-700 text-white text-xs flex-1 transition-all"
            onClick={() => onExport("glb")}
          >
            GLB
          </button>
          <button
            type="button"
            className="px-3 py-2 rounded bg-purple-600 hover:bg-purple-700 text-white text-xs flex-1 transition-all"
            onClick={() => onExport("stl")}
          >
            STL
          </button>
        </div>
      </div>
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange, onBlur }) {
  const clamp = (n) => Math.min(max, Math.max(min, n));
  const [inputValue, setInputValue] = useState(value);

  // Update input when slider value changes externally
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleNumberChange = (e) => {
    const raw = e.target.value;
    setInputValue(raw);
    const num = parseFloat(raw);
    if (!Number.isNaN(num)) {
      onChange(clamp(num));
    }
  };

  const handleInputBlur = (e) => {
    const num = parseFloat(e.target.value);
    if (Number.isNaN(num)) {
      setInputValue(value);
    } else {
      const clamped = clamp(num);
      setInputValue(clamped);
      onChange(clamped);
    }
    onBlur?.();
  };

  return (
    <div>
      <div className="flex justify-between items-center gap-2 mb-2">
        <span className="text-sm text-white font-medium flex-1">{label}</span>
        <div className="flex items-center gap-1">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={inputValue}
            onChange={handleNumberChange}
            onBlur={handleInputBlur}
            className="w-16 px-2 py-1.5 bg-orange-600 hover:bg-orange-500 text-white rounded border border-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-400 text-center font-semibold text-sm transition-colors"
          />
          <span className="text-xs text-gray-400 w-6">{label.includes("°") ? "°" : "mm"}</span>
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => {
          const newVal = parseFloat(e.target.value);
          setInputValue(newVal);
          onChange(newVal);
        }}
        onBlur={onBlur}
        className="w-full accent-orange-500 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
        style={{
          backgroundImage: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${((value - min) / (max - min)) * 100}%, #374151 ${((value - min) / (max - min)) * 100}%, #374151 100%)`
        }}
      />
    </div>
  );
}
